const DB_NAME = 'MrsBectorsOfflineDB';
const DB_VERSION = 4;

export const STORES = {
    ALC_SESSIONS: 'alc_sessions',
    ALC_CHECKPOINTS: 'alc_checkpoints',
    FOODSAFETY_SESSIONS: 'foodsafety_sessions',
    FOODSAFETY_CHECKPOINTS: 'foodsafety_checkpoints',
    CCP_OPRP_SESSIONS: 'ccp_oprp_sessions',
    CCP_OPRP_CHECKPOINTS: 'ccp_oprp_checkpoints',
    SIEVES_MAGNETS_CHECKPOINTS: 'sieves_magnets_checkpoints',
    MIXING_BAKING_SESSIONS: 'mixing_baking_sessions',
    MIXING_BAKING_CYCLES: 'mixing_baking_cycles',
    CONFIG: 'config_store',
    SYNC_QUEUE: 'sync_queue'
};

function getDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => reject(request.error);

        request.onsuccess = () => resolve(request.result);

        request.onupgradeneeded = (event: any) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORES.ALC_SESSIONS)) {
                db.createObjectStore(STORES.ALC_SESSIONS, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(STORES.ALC_CHECKPOINTS)) {
                const checkpointStore = db.createObjectStore(STORES.ALC_CHECKPOINTS, { keyPath: 'id' });
                checkpointStore.createIndex('tourId', 'tourId', { unique: false });
            }
            if (!db.objectStoreNames.contains(STORES.FOODSAFETY_SESSIONS)) {
                db.createObjectStore(STORES.FOODSAFETY_SESSIONS, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(STORES.FOODSAFETY_CHECKPOINTS)) {
                const fsCheckpoints = db.createObjectStore(STORES.FOODSAFETY_CHECKPOINTS, { keyPath: 'id' });
                fsCheckpoints.createIndex('tourId', 'tourId', { unique: false });
            }
            if (!db.objectStoreNames.contains(STORES.CCP_OPRP_SESSIONS)) {
                db.createObjectStore(STORES.CCP_OPRP_SESSIONS, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(STORES.CCP_OPRP_CHECKPOINTS)) {
                const ccpCheckpoints = db.createObjectStore(STORES.CCP_OPRP_CHECKPOINTS, { keyPath: 'id' });
                ccpCheckpoints.createIndex('tourId', 'tourId', { unique: false });
            }
            if (!db.objectStoreNames.contains(STORES.SIEVES_MAGNETS_CHECKPOINTS)) {
                const sievesCheckpoints = db.createObjectStore(STORES.SIEVES_MAGNETS_CHECKPOINTS, { keyPath: 'id' });
                sievesCheckpoints.createIndex('tourId', 'tourId', { unique: false });
            }
            if (!db.objectStoreNames.contains(STORES.MIXING_BAKING_SESSIONS)) {
                db.createObjectStore(STORES.MIXING_BAKING_SESSIONS, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(STORES.MIXING_BAKING_CYCLES)) {
                const mbCycles = db.createObjectStore(STORES.MIXING_BAKING_CYCLES, { keyPath: 'id' });
                mbCycles.createIndex('tourId', 'tourId', { unique: false });
            }
            if (!db.objectStoreNames.contains(STORES.CONFIG)) {
                db.createObjectStore(STORES.CONFIG, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
                db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' });
            }
        };
    });
}

export async function getFromStore<T = any>(storeName: string, id: string): Promise<T | null> {
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.get(id);

        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
    });
}

export async function putToStore(storeName: string, item: any): Promise<void> {
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.put(item);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

export async function deleteFromStore(storeName: string, id: string): Promise<void> {
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.delete(id);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

export async function getAllFromStore<T = any>(storeName: string): Promise<T[]> {
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.getAll();

        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
    });
}

export async function enqueueSync(syncItem: any): Promise<void> {
    await putToStore(STORES.SYNC_QUEUE, syncItem);
}

export async function getCheckpointsForTour(tourId: string): Promise<any[]> {
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORES.ALC_CHECKPOINTS, 'readonly');
        const store = transaction.objectStore(STORES.ALC_CHECKPOINTS);
        const index = store.index('tourId');
        const request = index.getAll(tourId);

        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
    });
}

export async function getFoodSafetyCheckpointsForTour(tourId: string): Promise<any[]> {
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORES.FOODSAFETY_CHECKPOINTS, 'readonly');
        const store = transaction.objectStore(STORES.FOODSAFETY_CHECKPOINTS);
        const index = store.index('tourId');
        const request = index.getAll(tourId);

        request.onsuccess = () => {
            const list = (request.result || []).map(r => r.data || r);
            resolve(list);
        };
        request.onerror = () => reject(request.error);
    });
}

export async function getCCPOPRPCheckpointsForTour(tourId: string): Promise<any[]> {
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORES.CCP_OPRP_CHECKPOINTS, 'readonly');
        const store = transaction.objectStore(STORES.CCP_OPRP_CHECKPOINTS);
        const index = store.index('tourId');
        const request = index.getAll(tourId);

        request.onsuccess = () => {
            const list = (request.result || []).map(r => r.data || r);
            resolve(list);
        };
        request.onerror = () => reject(request.error);
    });
}

export async function getSievesMagnetsCheckpointsForTour(tourId: string): Promise<any[]> {
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORES.SIEVES_MAGNETS_CHECKPOINTS, 'readonly');
        const store = transaction.objectStore(STORES.SIEVES_MAGNETS_CHECKPOINTS);
        const index = store.index('tourId');
        const request = index.getAll(tourId);

        request.onsuccess = () => {
            const list = (request.result || []).map(r => r.data || r);
            resolve(list);
        };
        request.onerror = () => reject(request.error);
    });
}

export async function getMixingBakingCyclesForTour(tourId: string): Promise<any[]> {
    const db = await getDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORES.MIXING_BAKING_CYCLES, 'readonly');
        const store = transaction.objectStore(STORES.MIXING_BAKING_CYCLES);
        const index = store.index('tourId');
        const request = index.getAll(tourId);

        request.onsuccess = () => {
            const list = (request.result || []).map(r => r.data || r);
            resolve(list);
        };
        request.onerror = () => reject(request.error);
    });
}

